import { useMemo } from 'react';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { usePath } from 'crossroad';
import { usePassageState } from '@/lib/hooks/use-passage-state';
import { usePassage } from '@/lib/hooks/use-passage';
import { PassageSelector } from '@/components/passage-selector';
import { StudyModeToggle } from '@/components/reading/study-mode-toggle';
import { type BibleBook } from '@/lib/utils/bible-utils';
import { ManuscriptSelectorDropdown } from '@/components/manuscript/manuscript-selector-dropdown';
import { useHydrationStage } from '@/lib/hooks/use-hydration-stage';
import { useUserSettings } from '@/lib/hooks/use-user-settings';
import { Walkthrough } from './ui/walkthrough';
import { useState } from 'react';

export default function AppHeader() {
    const { scrollState: { book, chapter }, passageId } = usePassageState();
    const { section: passage, isLoading } = usePassage();
    const { isRendering } = useHydrationStage();
    const { settings } = useUserSettings();
    const [showWalkthrough, setShowWalkthrough] = useState(true);
    const [path] = usePath();
    const isAdminPage = path?.startsWith('/app/manage');

    const currentBook = useMemo(() => {
        return (passage?.book || book) as BibleBook | null;
    }, [passage?.book, book]);

    const currentChapter = useMemo(() => {
        return passage?.ref ? 0 : chapter;
    }, [passage?.ref, chapter]);

    const title = useMemo(() => {
        // Don't show title until we have actual data
        if (!currentBook || currentChapter === null) {
            return null;
        }
        return `${currentBook} ${passage?.ref || currentChapter}`;
    }, [passage?.ref, currentBook, currentChapter]);

    const titleAndRef = useMemo(() => {
        if (!title) return null;
        return (
            <div className={`flex flex-col items-start min-w-0`}>
                <PassageSelector currentBook={currentBook!} currentChapter={currentChapter!} currentPassageId={passageId}>
                    <span id="tour-passage-selector" className="font-medium text-primary truncate">
                        {title}
                    </span>
                </PassageSelector>
            </div>
        )
    }, [title, currentBook, currentChapter, passageId]);

    return (
        <header className="bg-background/75 backdrop-blur-xl bg-background sticky top-0 z-50 flex w-full h-(--header-height) shrink-0 items-center gap-2 border-b shadow-lg transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-(--header-height)">
            <div className="flex w-full items-center justify-between gap-1 px-4 lg:gap-2 lg:px-6">
                <div className="flex items-center gap-1 lg:gap-2 flex-1 min-w-0">
                    <SidebarTrigger className="-ml-1" />

                    {!isLoading && !isAdminPage && titleAndRef}
                </div>

                <div className="flex items-center gap-1 lg:gap-2">
                    {isRendering && <ManuscriptSelectorDropdown />}
                    {!isAdminPage && <StudyModeToggle id="tour-dig-mode" />}
                </div>
            </div>

            {settings && !settings.hasSeenWalkthrough && showWalkthrough && (
                <Walkthrough
                    userId={settings.userId}
                    onDismiss={() => setShowWalkthrough(false)}
                />
            )}
        </header>
    );
}